// client/src/session/source.ts — the token and claims source · AUTH-004.
//
// This is the file that replaced `stub.ts` (D-330): the shell imports `useSession` from
// `../session`, and behind the barrel this module is what turns responses into a `Session`.
// Four responsibilities, and the order matters:
//
//   1. establishSession — a `SessionResponse` (login, register, verify, switch, refresh) holds
//      the token; `GET /users/me` supplies what the token does not carry (02 §6.2); together
//      they become one `Session` written to the store in a single `set`.
//   2. refreshSession — 04 §9.3.3 verbatim: concurrent 401s coalesce into ONE refresh, because
//      two parallel rotations of a rotating refresh token look like reuse and revoke the family.
//   3. ensureSession — the bootstrap a hard refresh needs: try the cookie once, remember the
//      answer, and let every guard await the same promise.
//   4. signOut / logoutHard — the transitions the shell and the http wrapper call, each
//      ending in a store write the components observe. (`switchOrganization` left with the
//      chooser and the header switcher, D-1542: a session reads every membership at once.)
//
// The two transports are configured from here (`connectHttp`) rather than importing the store
// themselves: `api/http.ts` (the hand-written wrapper `authApi` still rides on) and
// `api/configure.ts` (the generated client of 04 §9.3.2, which every resource screen uses)
// each stay a transport with four hooks, and this is the one module that decides what those
// hooks do — the SAME hooks for both, so there is one token, one refresh and one logout.

import type { QueryClient } from '@tanstack/react-query';

import { authApi, type SessionResponse } from '@/api/auth';
import { configureApiClient } from '@/api/configure';
import { configureHttp, type HttpHooks } from '@/api/http';
import { useAuthStore } from '@/stores/auth-store';

import { toSession } from './mapping';
import type { Session } from './types';

/** Refresh this far ahead of expiry, so a request never leaves with a token that dies in flight. */
const REFRESH_LEEWAY_MS = 30_000;

/**
 * Holds the issued token and completes the identity from `/users/me`.
 *
 * The token is written FIRST, on its own, so that the `/users/me` call it authorizes can be
 * made; the session follows in one `set` once the profile is known. A failure in between
 * leaves the store anonymous — half a session is not a state the shell renders.
 */
export async function establishSession(issued: SessionResponse): Promise<Session> {
  useAuthStore.getState().setAccessToken(issued.accessToken, issued.expiresAtUtc);

  try {
    const session = toSession(await authApi.me());
    useAuthStore.getState().setAuthenticated(issued.accessToken, issued.expiresAtUtc, session);
    return session;
  } catch (error) {
    useAuthStore.getState().setAnonymous();
    throw error;
  }
}

/**
 * Re-reads the profile under the token already held, and writes it back.
 *
 * The one caller is the invitation page (SCR-AUTH-006): accepting creates a membership, and
 * `organizations` on the session was read before it existed. The access token is unchanged and
 * is not re-issued — its claims are the DEFAULT organization's and the acceptance did not move
 * that (D-1542, D-1545) — so what is stale is exactly the profile, and only that is refetched.
 *
 * Silent on failure: the membership exists whatever this call did, and a page that had just
 * succeeded must not report an error because a second, cosmetic read did not.
 */
export async function reloadSession(): Promise<Session | null> {
  const { status, accessToken, accessTokenExpiresAt } = useAuthStore.getState();
  if (status !== 'authenticated' || accessToken === undefined || accessTokenExpiresAt === undefined) {
    return null;
  }

  try {
    const session = toSession(await authApi.me());

    // `setAuthenticated` re-parses the expiry from an ISO string, and the store holds epoch
    // milliseconds — so the instant is handed back in the form the setter takes rather than
    // the form it kept, and the token's lifetime is unchanged by the round trip.
    useAuthStore
      .getState()
      .setAuthenticated(accessToken, new Date(accessTokenExpiresAt).toISOString(), session);
    return session;
  } catch {
    return useAuthStore.getState().session;
  }
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * 04 §9.3.3. Concurrent 401s coalesce into ONE refresh; rotating refresh tokens with
 * reuse-detection (02 §6.3) make parallel refresh calls actively harmful — a second rotation
 * attempt looks like token reuse and revokes the family.
 */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= doRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function doRefresh(): Promise<boolean> {
  try {
    const issued = await authApi.refresh();
    const current = useAuthStore.getState().session;

    // A refresh continues the same session under the same organization, so the profile in
    // hand is still true and the swap is invisible (04 §9.3.3). Anything else — a bootstrap,
    // or an organization the store has never seen — reads the profile again.
    if (current && current.activeOrganizationId === issued.activeOrganizationId) {
      useAuthStore.getState().setAuthenticated(issued.accessToken, issued.expiresAtUtc, current);
    } else {
      await establishSession(issued);
    }
    return true;
  } catch {
    useAuthStore.getState().setAnonymous();
    return false;
  }
}

let bootstrap: Promise<Session | null> | null = null;

/**
 * The session, after the refresh cookie has been given one chance. Route guards await this
 * (04 §9.2.2); components never do, because by the time a component under `/app` renders the
 * guard has already resolved it.
 */
export function ensureSession(): Promise<Session | null> {
  const state = useAuthStore.getState();
  if (state.status !== 'unknown') return Promise.resolve(state.session);

  bootstrap ??= refreshSession()
    .then(() => useAuthStore.getState().session)
    .finally(() => {
      bootstrap = null;
    });
  return bootstrap;
}

/** Refreshes ahead of expiry so a request never carries a token that is about to be refused. */
async function ensureFreshToken(): Promise<void> {
  const { status, accessTokenExpiresAt } = useAuthStore.getState();
  if (status !== 'authenticated' || accessTokenExpiresAt === undefined) return;
  if (Date.now() >= accessTokenExpiresAt - REFRESH_LEEWAY_MS) await refreshSession();
}

/**
 * Deliberate sign-out: revoke the family (02 §6.3), then forget everything. The server call
 * is best-effort — a network failure must not leave the user looking signed in.
 */
export async function signOut(queryClient: QueryClient): Promise<void> {
  try {
    await authApi.logout();
  } catch {
    // The family will expire on its own; the local session ends now regardless.
  }
  logoutHard(queryClient);
}

/**
 * 04 §9.3.2: "clears the auth store AND calls queryClient.clear()" — the same cache hygiene as
 * org switching. Every cached row was fetched under a session that no longer exists.
 */
export function logoutHard(queryClient: QueryClient): void {
  useAuthStore.getState().setAnonymous();
  useAuthStore.getState().setPendingMfa(null);
  queryClient.clear();
}

/**
 * Wires both transports to this module. Called once from the composition root (§9.1) with
 * what a 401 that cannot be refreshed should do — navigate to `/login` with the deep link.
 */
export function connectHttp(queryClient: QueryClient, onUnauthenticated: () => void): void {
  const hooks: HttpHooks = {
    getAccessToken: () => useAuthStore.getState().accessToken,
    ensureFreshToken,
    refreshSession,
    onUnauthenticated: () => {
      logoutHard(queryClient);
      onUnauthenticated();
    },
  };
  configureHttp(hooks);
  configureApiClient(hooks);
}
